"use client";

import clsx from "clsx";
import React, { type InputHTMLAttributes, useEffect, useRef, useState } from "react";
import { composeRefs } from "../../../lib/composeRefs";
import type { ControlSize } from "../../../lib/FrontendTypes";
import { useDebouncedCallback } from "../../../lib/useDebouncedCallback";
import { inputBoxClassName } from "../InputBox/InputBox";
import type { InputProps } from "../InputTypes";
import "./TextInput.css";

const styles: Record<string, string> = {
  control: `${inputBoxClassName} calmlens-textinput-control`,
};

export const textInputClassName = styles.control;

export type TextInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size"> &
  InputProps<string | number> & {
    size?: ControlSize;
    showValidation?: boolean;
    onValueChange?: (value: any) => void;
    ref?: React.Ref<HTMLInputElement>;
  };

const TextInput = (props: TextInputProps) => {
  const {
    className,
    onValueProcess,
    autoFocus,
    onBlur,
    ref,
    onChange,
    size,
    showValidation = true,
    onValueChange,
    value,
    defaultValue,
    type,
    ...otherProps
  } = props;
  const ownInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) {
      ownInputRef.current?.focus();
    }
  }, [autoFocus]);

  const [valid, setValid] = useState<boolean>(true);

  const checkValidity = useDebouncedCallback(
    () => {
      setValid(ownInputRef.current?.checkValidity() ?? true);
    },
    [],
    500,
  );

  return (
    <input
      type={type}
      data-size={size}
      onBlur={(e) => {
        onBlur?.(e);
        checkValidity.immediate();
      }}
      ref={composeRefs(ref, ownInputRef)}
      className={clsx(styles.control, className)}
      onChange={(e) => {
        let processedValue: string | number | undefined = e.target.value;
        if (type === "number") {
          // Browsers report "" for input that isn't a number yet ("12.", "-", an empty field):
          // that is no value, not NaN, and the field keeps what was typed.
          const parsed = Number.parseFloat(e.target.value);
          processedValue = Number.isFinite(parsed) ? parsed : undefined;
        }

        if (onValueProcess && processedValue !== undefined) {
          processedValue = onValueProcess(processedValue);
        }
        if (type !== "number" && typeof processedValue === "string") {
          e.target.value = processedValue;
        }
        onValueChange?.(processedValue);
        onChange?.(e);
        setValid(true);
        checkValidity();
      }}
      value={value?.toString()}
      defaultValue={defaultValue?.toString()}
      {...otherProps}
      aria-invalid={otherProps["aria-invalid"] ?? (showValidation && !valid ? true : undefined)}
      data-invalid={otherProps["aria-invalid"] || (showValidation && !valid) ? "true" : undefined}
    />
  );
};

export default TextInput;
